import { useState } from 'react';
import { Trash2, Upload } from 'lucide-react';
import Table from '@/components/table/components/Table.tsx';
import EmptyTable from '@/components/table/components/EmptyTable.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import rawBankTransactionsApi from '@/features/dashboard/rawBankTransactions/api/rawBankTransactionsApi.ts';
import { rawBankTransactionsApiQueryKeys } from '@/features/dashboard/rawBankTransactions/api/rawBankTransactionsApi.queryKeys.ts';
import { RowContextMenuItemParams } from '@/components/table/components/RowContextMenu.tsx';
import { BulkAction } from '@/components/table/types/BulkAction.ts';
import { RawTransactionListDto } from '@/features/dashboard/rawBankTransactions/types/RawTransactionListDto.ts';
import { RawTransactionStatus } from '@/features/dashboard/rawBankTransactions/types/RawTransactionStatus.ts';
import useIgnoreTransaction from '@/features/dashboard/rawBankTransactions/hooks/useIgnoreTransaction.ts';
import useDeleteRawTransactions from '@/features/dashboard/rawBankTransactions/hooks/useDeleteRawTransactions.ts';
import {
  deleteTransactionDescription,
  deleteTransactionsTitle,
} from '@/features/dashboard/rawBankTransactions/utils/deleteTransactionCopy.ts';
import useRevertTransaction from '@/features/dashboard/finances/hooks/useRevertTransaction.ts';
import { confirmDialogUseHook } from '@/components/ui/ConfirmDialog.useHook.ts';
import BankImportModal from '@/features/dashboard/rawBankTransactions/components/BankImportModal.tsx';
import QualifyAsExpenseModal from '@/features/dashboard/rawBankTransactions/components/QualifyAsExpenseModal.tsx';
import MatchTransactionModal from '@/features/dashboard/rawBankTransactions/components/MatchTransactionModal.tsx';

const RawTransactionsTable = () => {
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [transactionToQualify, setTransactionToQualify] =
    useState<RawTransactionListDto | null>(null);
  const [transactionToMatch, setTransactionToMatch] =
    useState<RawTransactionListDto | null>(null);
  const ignoreTransactionMutation = useIgnoreTransaction();
  const revertMutation = useRevertTransaction();
  const deleteMutation = useDeleteRawTransactions();
  const { confirmWithCallback, confirmDeleteWithCallback } =
    confirmDialogUseHook();

  const isMatched = (t: RawTransactionListDto) =>
    t.status === RawTransactionStatus.MATCHED_AUTO ||
    t.status === RawTransactionStatus.MATCHED_MANUAL;

  const rowContextMenuItems: RowContextMenuItemParams<RawTransactionListDto>[] =
    [
      {
        label: 'Zakwalifikuj jako wpłatę',
        // Backend odbija ujemną pozycję („Only income transactions…").
        visible: (t) => t.amount.amount > 0 && !isMatched(t),
        disabled: (t) => t.status === RawTransactionStatus.IGNORED,
        disabledTooltip: () =>
          'Najpierw cofnij zignorowanie, żeby zakwalifikować tę transakcję',
        action: (transaction) => setTransactionToMatch(transaction),
      },
      {
        label: 'Zakwalifikuj jako wydatek',
        // Backend odbija dodatnią pozycję (`RAW_TRANSACTION_NOT_AN_EXPENSE`).
        visible: (t) => t.amount.amount < 0 && !isMatched(t),
        disabled: (t) => t.status === RawTransactionStatus.IGNORED,
        disabledTooltip: () =>
          'Najpierw cofnij zignorowanie, żeby zakwalifikować tę transakcję',
        action: (transaction) => setTransactionToQualify(transaction),
      },
      {
        label: 'Cofnij kwalifikację',
        visible: isMatched,
        action: (transaction) =>
          confirmWithCallback({
            title: 'Cofnąć kwalifikację?',
            description:
              'Wpięta zapłata zostanie odpięta, a pozycja wróci do oczekujących.',
            confirmText: 'Cofnij kwalifikację',
            cancelText: 'Wróć',
            onConfirm: async () => {
              await revertMutation.mutateAsync(transaction.id);
            },
          }),
      },
      {
        label: 'Ignoruj',
        visible: (t) =>
          t.status !== RawTransactionStatus.IGNORED && !isMatched(t),
        action: (transaction) =>
          ignoreTransactionMutation.mutate({ id: transaction.id }),
      },
      {
        // Backend odrzuca revert tylko dla PENDING, więc zignorowana pozycja przechodzi.
        label: 'Cofnij zignorowanie',
        visible: (t) => t.status === RawTransactionStatus.IGNORED,
        action: (transaction) =>
          confirmWithCallback({
            title: 'Cofnąć zignorowanie?',
            description:
              'Przelew wróci na listę oczekujących i znów będzie można go zakwalifikować.',
            confirmText: 'Cofnij zignorowanie',
            cancelText: 'Wróć',
            onConfirm: async () => {
              await revertMutation.mutateAsync(transaction.id);
            },
          }),
      },
      {
        label: 'Usuń',
        variant: 'destructive',
        // Wpięty wiersz backend odrzuca — jego ścieżką jest „Cofnij kwalifikację”.
        visible: (t) => !isMatched(t),
        action: (transaction) =>
          confirmDeleteWithCallback({
            title: 'Usuń przelew',
            description: deleteTransactionDescription(transaction),
            confirmText: 'Usuń',
            cancelText: 'Wróć',
            onConfirm: async () => {
              await deleteMutation.mutateAsync([transaction.id]);
            },
          }),
      },
    ];

  const bulkActions: BulkAction<RawTransactionListDto>[] = [
    {
      id: 'delete-raw-transactions',
      label: 'Usuń zaznaczone',
      icon: Trash2,
      variant: 'danger',
      disabled: (rows) => rows.some(isMatched),
      disabledTooltip: () =>
        'W zaznaczeniu jest wpięty przelew — najpierw cofnij jego kwalifikację',
      confirm: (rows) => ({
        title: deleteTransactionsTitle(rows.length),
        description:
          'Zaznaczone przelewy znikną z listy zaimportowanych. Tej operacji nie można cofnąć.',
        confirmText: 'Usuń',
        variant: 'destructive',
      }),
      // Paczka jest wszystko-albo-nic, więc po błędzie zaznaczenie musi zostać —
      // stąd własne czyszczenie zamiast domyślnego, które czyści zawsze.
      keepSelectionOnComplete: true,
      onClick: async (rows, ctx) => {
        try {
          await deleteMutation.mutateAsync(rows.map((row) => row.id));
          ctx.clearSelection();
        } catch {
          // Komunikat pokazuje `useDeleteRawTransactions`; drugi toast z paska
          // powtarzałby tę samą treść.
        }
      },
    },
  ];

  return (
    <>
      <Table
        queryKey={rawBankTransactionsApiQueryKeys.raw()}
        fetchData={rawBankTransactionsApi.getAll}
        fetchTableDef={rawBankTransactionsApi.getTableDefinition}
        rowContextMenuItems={rowContextMenuItems}
        bulkActions={bulkActions}
        emptyDataComponent={
          <EmptyTable
            title="Brak zaimportowanych płatności"
            description="Możesz samodzielnie zarejestrować wpłatę lub zaimportować listę wpłat z Twojego banku."
            icon={<Upload />}
          >
            <ActionButton
              tone="outline"
              size="xs"
              onClick={() => setIsImportModalOpen(true)}
            >
              <Upload />
              Importuj z banku
            </ActionButton>
          </EmptyTable>
        }
      />

      {isImportModalOpen && (
        <BankImportModal onClose={() => setIsImportModalOpen(false)} />
      )}

      {transactionToQualify && (
        <QualifyAsExpenseModal
          transaction={transactionToQualify}
          onClose={() => setTransactionToQualify(null)}
        />
      )}

      {transactionToMatch && (
        <MatchTransactionModal
          transaction={transactionToMatch}
          onClose={() => setTransactionToMatch(null)}
        />
      )}
    </>
  );
};

export default RawTransactionsTable;
